// ---------------------------------------------------------------------------
// ConversationView — chat-bubble renderer for a sequence of messages.
//
// Shape-agnostic: takes a normalized `ConversationMessage[]` (author, content,
// timestamp, role, replyTo, optional badges). Adapter code in the renderer
// translates synthesis-specific message shapes into this normalized form.
// Reply indentation is rendered when `replyTo` is set; otherwise messages
// flow top-to-bottom.
// ---------------------------------------------------------------------------

import { mergeClasses } from '@fluentui/react-components';
import { useMemo } from 'react';

import type { ConversationViewProps } from './ConversationView.interface.js';
import { useStyles } from './ConversationView.style.js';

export type {
  ConversationBadge,
  ConversationMessage,
  ConversationViewProps,
} from './ConversationView.interface.js';

/**
 * Avatar background colors, cycled in order as new authors are encountered.
 * Hand-picked to be distinct under both light and dark themes.
 */
const AVATAR_COLORS: readonly string[] = [
  '#6c6cf0', // indigo
  '#50c878', // emerald
  '#f0a050', // orange
  '#5090f0', // blue
  '#c070d0', // magenta
  '#40b0b0', // teal
  '#d0a040', // amber
  '#f06080', // rose
];

function pickAvatarColor(index: number): string {
  return AVATAR_COLORS[index % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]!;
}

export function ConversationView({ messages }: ConversationViewProps) {
  const styles = useStyles();

  // Stable color per author for the duration of the render.
  const authorColors = useMemo(() => {
    const map = new Map<string, string>();
    let i = 0;
    for (const m of messages) {
      if (!map.has(m.author)) {
        map.set(m.author, pickAvatarColor(i));
        i += 1;
      }
    }
    return map;
  }, [messages]);

  return (
    <div className={styles.root}>
      {messages.map((m) => {
        const color = authorColors.get(m.author) ?? pickAvatarColor(0);
        const isReply = m.replyTo !== undefined && m.replyTo !== null;
        return (
          <div
            key={m.id}
            className={mergeClasses(
              styles.msg,
              isReply ? styles.reply : undefined,
            )}
          >
            <div
              className={styles.avatar}
              style={{ backgroundColor: color }}
              aria-hidden
            >
              {initials(m.author)}
            </div>
            <div className={styles.body}>
              <div className={styles.headerLine}>
                <span className={styles.author} style={{ color }}>
                  {m.author}
                </span>
                {m.role && <span className={styles.role}>{m.role}</span>}
                {m.timestamp && (
                  <span className={styles.time}>
                    {formatShort(m.timestamp)}
                  </span>
                )}
              </div>
              <div className={styles.content}>{m.content}</div>
              {m.badges && m.badges.length > 0 && (
                <div className={styles.badges}>
                  {m.badges.map((b, i) => (
                    <span
                      key={`${b.label}-${i}`}
                      className={mergeClasses(
                        styles.badge,
                        b.kind === 'noise' ? styles.badgeNoise : undefined,
                        b.kind === 'decision'
                          ? styles.badgeDecision
                          : undefined,
                        b.kind === 'phase' ? styles.badgePhase : undefined,
                      )}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function initials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function formatShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
